import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/store/authStore';
import { useSignalsStore } from '@/store/signalsStore';
import { useTelegram } from './useTelegram';
import type { SignalCurrentPriceEvent, NotificationEvent } from '@/api/types';

// Use same base URL as API
const SOCKET_URL = import.meta.env.VITE_API_BASE_URL || '';

interface UseWebSocketReturn {
  isConnected: boolean;
  mainCurrencyPrice: number | null;
  reconnect: () => void;
}

export const useWebSocket = (): UseWebSocketReturn => {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [mainCurrencyPrice, setMainCurrencyPrice] = useState<number | null>(null);

  const { user } = useAuthStore();
  const { updateCurrentPrice } = useSignalsStore();
  const { showAlert, notificationOccurred } = useTelegram();

  // Handle signal price update
  const handleSignalCurrentPrice = useCallback(
    (data: SignalCurrentPriceEvent) => {
      updateCurrentPrice(data.signalId, data.price);
    },
    [updateCurrentPrice]
  );

  // Handle notification
  const handleNotification = useCallback(
    (data: NotificationEvent) => {
      // Show haptic feedback
      notificationOccurred('success');

      // Show alert for price alerts
      if (data.type === 'PRICE_ALERT' && data.signalInfo) {
        showAlert(`${data.title}\n\n${data.message}`);
      }
    },
    [showAlert, notificationOccurred]
  );

  // Handle connection
  const handleConnect = useCallback(() => {
    console.log('WebSocket connected');
    setIsConnected(true);
  }, []);

  // Handle disconnection
  const handleDisconnect = useCallback((reason: string) => {
    console.log('WebSocket disconnected:', reason);
    setIsConnected(false);
  }, []);

  // Handle connection error
  const handleError = useCallback((error: Error) => {
    console.error('WebSocket error:', error);
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!user?.id) {
      console.log('No user ID, skipping WebSocket connection');
      return;
    }

    // Disconnect existing socket
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    // Create new socket connection - same as React Native app
    const socket = io(`${SOCKET_URL}?userId=${user.id}`);

    socketRef.current = socket;

    // Event listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleError);
    socket.on('signalCurrentPrice', handleSignalCurrentPrice);
    socket.on('notification', handleNotification);

    // Listen for main currency price (BTC/USDT live price)
    socket.on('main-currency-price', (data: { price: number }) => {
      if (data?.price) {
        setMainCurrencyPrice(data.price);
      }
    });
  }, [user?.id, handleConnect, handleDisconnect, handleError, handleSignalCurrentPrice, handleNotification]);

  // Reconnect function
  const reconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    connect();
  }, [connect]);

  // Connect when user is authenticated
  useEffect(() => {
    if (user?.id) {
      connect();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.off('connect', handleConnect);
        socketRef.current.off('disconnect', handleDisconnect);
        socketRef.current.off('connect_error', handleError);
        socketRef.current.off('signalCurrentPrice', handleSignalCurrentPrice);
        socketRef.current.off('notification', handleNotification);
        socketRef.current.disconnect();
      }
    };
  }, [user?.id, connect, handleConnect, handleDisconnect, handleError, handleSignalCurrentPrice, handleNotification]);

  return {
    isConnected,
    mainCurrencyPrice,
    reconnect,
  };
};
